import React, { FC, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    AccordionDetails,
    Grid,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import PageContainer from "../../../components/pageContainer";
import { formatDate } from "../../../utils/datetimeUtils";
import { PATHS } from "../../../routes/paths";
import { StyledAccordion, StyledAccordionSummary, StyledTableCell } from "../../../styles/commonStyles";
import { ExpandMore } from "@mui/icons-material";
import {
    getSalesPersonAdminSalesPersonsSalesPersonIdGet,
    updateSalesPersonAdminSalesPersonsSalesPersonIdPatch,
    deleteSalesPersonAdminSalesPersonsSalesPersonIdDelete
} from "../../../client/services.gen";
import { SalesPersonResponse, SalesPersonUpdate } from "../../../client/types.gen";

const SalesPersonDetail: FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState<SalesPersonResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();

    const fetchSalesPerson = async () => {
        if (!id) return;

        setLoading(true);
        setError(null);
        try {
            const response = await getSalesPersonAdminSalesPersonsSalesPersonIdGet({ salesPersonId: Number(id) });
            setData(response);
        } catch (err) {
            setError('営業担当者の取得に失敗しました');
            console.error('Failed to fetch sales person:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesPerson();
    }, [id]);

    const handleSave = async () => {
        if (!data || !id) return;

        setSaving(true);
        setError(null);
        try {
            const updateData: SalesPersonUpdate = {
                name: data.name
            };

            const response = await updateSalesPersonAdminSalesPersonsSalesPersonIdPatch({
                salesPersonId: Number(id),
                requestBody: updateData
            });

            setData(response);
            setIsEditing(false);
        } catch (err) {
            setError('営業担当者の更新に失敗しました');
            console.error('Failed to update sales person:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!id) return;

        setSaving(true);
        setError(null);
        try {
            await deleteSalesPersonAdminSalesPersonsSalesPersonIdDelete({ salesPersonId: Number(id) });
            navigate(PATHS.SALES_PERSON_LIST);
        } catch (err) {
            setError('営業担当者の削除に失敗しました');
            console.error('Failed to delete sales person:', err);
            setSaving(false);
        }
        setDeleteDialogOpen(false);
    };

    const handleCancel = () => {
        fetchSalesPerson();
        setIsEditing(false);
        setError(null);
    };

    if (loading) {
        return (
            <PageContainer>
                <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                    <CircularProgress />
                </Grid>
            </PageContainer>
        );
    }

    if (!data) {
        return (
            <PageContainer>
                <Alert severity="error">営業担当者が見つかりません</Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* ヘッダー */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" component="div">営業担当者詳細</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {isEditing ? (
                        <>
                            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                                キャンセル
                            </Button>
                            <Button variant="contained" onClick={handleSave} disabled={saving}>
                                {saving ? <CircularProgress size={20} /> : '保存'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setDeleteDialogOpen(true)}
                            >
                                削除
                            </Button>
                            <Button variant="contained" onClick={() => setIsEditing(true)}>
                                編集
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {/* 基本情報 */}
            <StyledAccordion defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">基本情報</Typography>
                </StyledAccordionSummary>
                <AccordionDetails>
                    <Table>
                        <TableBody>
                        <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                                営業担当者名
                            </StyledTableCell>
                            <TableCell>
                                {isEditing ? (
                                    <TextField
                                        value={data.name}
                                        onChange={(e) => setData({ ...data, name: e.target.value })}
                                        fullWidth
                                        required
                                        variant="outlined"
                                        size="small"
                                    />
                                ) : (
                                    data.name
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                                作成日
                            </StyledTableCell>
                            <TableCell>{formatDate(data.created_at)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                                更新日
                            </StyledTableCell>
                            <TableCell>{formatDate(data.updated_at)}</TableCell>
                        </TableRow>
                        </TableBody>
                    </Table>
                </AccordionDetails>
            </StyledAccordion>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>削除確認</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        「{data.name}」を削除してもよろしいですか？
                        この操作は取り消せません。
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
                    <Button onClick={handleDelete} color="error" disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : '削除'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default SalesPersonDetail;